import { CommentInput } from '@/components/comments/CommentInput';
import { Comment } from '@/components/comments/Comment';
import { Comment as CommentDto } from '@/actions/dtos/tender/comment';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createComment, deleteComment, editComment, getCommentsByTenderId } from '@/api/requests/comments';

const COMMENTS_COUNT_PER_PAGE = 10;

interface CommentSectionProps {
  tenderId: string;
}

export const CommentSection: React.FC<CommentSectionProps> = ({ tenderId }) => {
  const [comments, setComments] = useState<CommentDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const hasMore = useRef<boolean>(true);
  const observerRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const fetchComments = useCallback(
    async (pageNumber: number) => {
      try {
        setIsLoading(true);
        const commentsByTenderId = await getCommentsByTenderId(tenderId, pageNumber, COMMENTS_COUNT_PER_PAGE);
        if (commentsByTenderId.content.length < COMMENTS_COUNT_PER_PAGE) {
          hasMore.current = false;
        }

        setComments((prev) => {
          if (pageNumber === 0) {
            return commentsByTenderId.content;
          } else {
            return [...prev, ...commentsByTenderId.content];
          }
        });
      } finally {
        setIsLoading(false);
      }
    },
    [tenderId],
  );

  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      await fetchComments(0);
    } finally {
      setIsLoading(false);
    }
  }, [fetchComments]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData, tenderId]);

  const resetComments = () => {
    setPage(0);
    hasMore.current = true;
  };

  const handleCreateComment = async (tenderId: string, message: string) => {
    try {
      setIsLoading(true);
      await createComment(tenderId, message);
      resetComments();
      fetchComments(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditComment = async (commentId: string, message: string) => {
    try {
      setIsLoading(true);
      const originalCommentMessage = comments.filter((comment) => comment.id === commentId)?.[0].message;
      if (originalCommentMessage?.trim() !== message) {
        await editComment(commentId, message);

        resetComments();
        fetchComments(0);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      setIsLoading(true);
      await deleteComment(commentId);

      resetComments();
      fetchComments(0);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreComments = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore.current && !isLoading) {
        setPage((prevPage) => prevPage + 1);
      }
    },
    [isLoading],
  );

  useEffect(() => {
    if (page > 0) {
      fetchComments(page);
    }
  }, [fetchComments, page]);

  useEffect(() => {
    const observer = new IntersectionObserver(loadMoreComments, {
      root: containerRef.current,
      rootMargin: '0px',
      threshold: 1.0,
    });

    const currentObserverRef = observerRef.current;
    if (currentObserverRef) {
      observer.observe(currentObserverRef);
    }

    return () => {
      if (currentObserverRef) {
        observer.unobserve(currentObserverRef);
      }
    };
  }, [loadMoreComments]);

  return (
    <div className="relative flex flex-col">
      <div
        ref={containerRef}
        className="flex max-h-sm flex-col gap-5 overflow-y-auto p-1"
        style={{
          scrollbarWidth: 'none',
        }}
      >
        <CommentInput tenderId={tenderId} handleCreateComment={handleCreateComment} />
        {comments.map((comment) => (
          <Comment
            key={comment.id}
            comment={comment}
            handleDeleteComment={handleDeleteComment}
            handleEditComment={handleEditComment}
          />
        ))}
        {hasMore.current && <div ref={observerRef} className="h-2" />}
      </div>
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-base-100 bg-opacity-50">
          <span className="loading loading-spinner" />
        </div>
      )}
    </div>
  );
};
